import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, ValidationErrors, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { validateImage } from 'image-validator';
import { QuillEditorComponent } from 'ngx-quill';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { PublicationQuillEditorHelper } from '../../../../clubs/helpers/publication-quill-editor.helper';
import { ClubPublicationService } from '../../../../clubs/services/publication.service';
import { SelectedRoomsService } from '../../../../clubs/services/selected-rooms-service';
import { EmojiHelper } from '../../../../helpers/emoji.helper';
import { QuillEditorHelper } from '../../../../helpers/quill-editor.helper';
import { SpinnerHelper } from '../../../../helpers/spinner.helper';
import { ToastrHelper } from '../../../../helpers/toastr.helper';
import { Member } from '../../../../models/clubs/member.model';
import { MentionMember } from '../../../../models/clubs/mention-member.model';
import { MemberApiService } from '../../../../services/clubs/member.api.service';
import { PublicationService } from '../../../../services/clubs/publication.service';
import { DocumentsService } from '../../../../services/dms/documents.service';
import { AccountsService } from '../../../../services/identity/accounts.service';
import { WudoModalComponent } from '../../../../shared/wudo-components/wudo-modal/wudo-modal.component';
import { ClubsStore } from './../../../../clubs/clubs.store';
import { messages } from './../../../../constants/messages';
import { CreateMulitplePublicationCommand } from './../../../../models/clubs/create-multiple-publication.command';
import { Publication } from './../../../../models/clubs/publication.model';
import { User } from './../../../../models/identity/user.model';
import { CropperParams } from './../../../../models/shared/cropper-params.model';
import { ImageService } from './../../../../services/shared/image.service';

@Component({
    selector: 'create-publication-modal-button',
    styleUrls: ['./create-publication-modal-button.component.scss'],
    templateUrl: './create-publication-modal-button.component.html',
})
export class CreatePublicationButtonModalComponent implements OnInit, AfterViewInit, OnDestroy {
    private documentHasChanged = false;
    private isInitialImageLoad = true;

    private magicUrlEnabled = false;
    public readonly PUBLICATION_CONTENT_MAX_LENGTH = 5000;

    public LocalDocument: File;
    public action: string;
    @Output()
    public afterPublicationCreation = new EventEmitter<void>();
    @Output()
    public afterPublicationUpdate = new EventEmitter<Publication>();
    public canUserCreateAnnouncement: boolean;
    public clubMessageMapping = {
        '=0': this.translate.instant('clubs.page.common.createPublication.clubs.empty'),
        '=1': this.translate.instant('clubs.page.common.createPublication.clubs.club'),
        other: this.translate.instant('clubs.page.common.createPublication.clubs.clubs'),
    };
    public commentText: string;
    public cropperParams: CropperParams = new CropperParams();
    public currentUser: User;
    @Input() public disabled: boolean;
    public emojiTranslations: unknown;
    @Input() public existingPublication: Publication;
    public form = this.fb.group(
        {
            content: ['', [Validators.required, this.plainTextMaxLength(this.PUBLICATION_CONTENT_MAX_LENGTH)]],
            clubId: [null, [Validators.required]],
            userId: [null, []],
            postId: [null, []],
            image: [null, []],
            picture: [null, []],
            attachedDocumentId: [null, []],
            attachedUrl: [null, []],
            pictureHasChanged: [null, []],
            gif: [null, []],
            mentionMembers: [null, []],
            isAnAnnouncement: [false, [Validators.required]],
        },
        {
            validators: [this.validatePostContentOrPicture.bind(this), this.validateSelectedRooms.bind(this)],
        },
    );
    public hasPictureInBegining = false;
    @Input() public isEditMode = false;
    public isEmojiPickerVisible = false;
    public isMobile = false;
    public isModalDisplayed = false;
    public isTextShown = false;
    public localImageSrc = '';
    public members: Array<Member>;
    public messages = messages;
    @ViewChild('modal') public modal: WudoModalComponent;
    @ViewChild('newPublicationQuillEditor') public newPublicationQuillEditor: QuillEditorComponent;
    @ViewChild('documentInput') public documentInput: ElementRef;
    @ViewChild('imageInput') public imageInput: ElementRef;
    public pictureHasBeenDeleted = false;
    public pictureHasChanged = false;
    public pos: number;
    public roomMessageMapping = {
        '=0': this.translate.instant('clubs.page.common.createPublication.rooms.empty'),
        '=1': this.translate.instant('clubs.page.common.createPublication.rooms.room'),
        other: this.translate.instant('clubs.page.common.createPublication.rooms.rooms'),
    };
    public submitted = false;
    public title: string;
    public urlLink: string;
    public isLinkLoading = false;
    public isPanelSlided = false;
    constructor(
        private readonly accountService: AccountsService,
        private readonly breakpointObserver: BreakpointObserver,
        public readonly clubsStore: ClubsStore,
        private readonly documentService: DocumentsService,
        private readonly emojiHelper: EmojiHelper,
        private readonly fb: FormBuilder,
        public readonly imageService: ImageService,
        private readonly memberService: MemberApiService,
        private readonly publicationApiService: PublicationService,
        private readonly publicationService: ClubPublicationService,
        readonly publicationQuillEditorHelper: PublicationQuillEditorHelper,
        public readonly quillEditorHelper: QuillEditorHelper,
        private selectedRoomsService: SelectedRoomsService,
        private readonly spinnerHelper: SpinnerHelper,
        private readonly toastrHelper: ToastrHelper,
        private translate: TranslateService,
    ) {
        this.breakpointObserver.observe([Breakpoints.HandsetPortrait]).subscribe((result) => {
            this.isMobile = result.matches;
        });
    }

    public get announcementMessage(): string {
        return this.form.controls.isAnAnnouncement.value
            ? this.translate.instant('clubs.page.common.createPublication.announcementEnabled')
            : this.translate.instant('clubs.page.common.createPublication.announcementDisabled');
    }

    public get canUserPublish(): boolean {
        if (this.clubsStore.displayedRoom?.publicationsAreLimited) {
            if (this.clubsStore.isAdminOfDisplayedClub || this.clubsStore.isAnimatorOfDisplayedRoom) {
                return true;
            } else {
                return false;
            }
        } else {
            return true;
        }
    }

    public get contentLength(): number {
        if (!this.newPublicationQuillEditor) {
            return 0;
        }

        return this.quillEditorHelper.getPlainTextLength(this.form.controls.content?.value);
    }

    public get currentClubTitle(): string {
        return this.clubsStore.displayedClub.title ?? this.translate.instant('clubs.default.defaultRoomName');
    }

    public get currentRoomTitle(): string {
        return this.clubsStore.displayedRoom.title === this.clubsStore.displayedClub.title
            ? this.translate.instant('clubs.default.defaultRoomName')
            : this.clubsStore.displayedRoom.title;
    }

    public get formDoesNotContainContent(): boolean {
        return this.isContentEmpty() && this.isPictureEmpty();
    }

    public get isAnAnnouncement(): boolean {
        return this.form.controls.isAnAnnouncement?.value ?? false;
    }

    public get isEditingComment() {
        return this.isEditMode && this.existingPublication?.depth > 1;
    }

    public get isPortraitChallengeAvailable(): boolean {
        return (
            !this.clubsStore.member.hasCompletedCommunityPresentation &&
            this.clubsStore.displayedClub.communityId != null &&
            this.clubsStore.isPresentationRoom()
        );
    }

    public addEmojiToContent(data: any) {
        this.pos = this.quillEditorHelper.getCaretPositionQuill(this.newPublicationQuillEditor);

        const emoji = data?.emoji?.native;

        if (!emoji) {
            console.error("can't find emoji");
            return;
        }

        this.newPublicationQuillEditor.quillEditor.insertText(this.pos, emoji);
        this.newPublicationQuillEditor.quillEditor.setSelection(this.pos + emoji.length);
    }

    public addPortraitChallengeToTextArea() {
        this.newPublicationQuillEditor.quillEditor.root.innerHTML = this.translate.instant('clubs.page.tabs.discussion.post.portraitChallenge');
        this.isTextShown = true;
    }

    public changeAnnouncementValue() {
        this.form.controls.isAnAnnouncement.setValue(!this.form.controls.isAnAnnouncement.value);
    }

    public clearTextArea() {
        this.newPublicationQuillEditor.quillEditor.root.innerHTML = '';
        this.isTextShown = false;
    }

    public closeModal() {
        this.isEmojiPickerVisible = false;
        this.isModalDisplayed = false;
        this.members = [];
        this.resetForm();
        this.clearFileInputs();
    }

    public getSelectedClubCount(): number {
        const selectedRooms = this.selectedRoomsService.selectedRoomsByClubIdSubject.value;
        return Object.keys(selectedRooms).filter((clubId) => selectedRooms[clubId].length > 0).length;
    }

    public getTotalSelectedRooms(): number {
        const selectedRooms = this.selectedRoomsService.selectedRoomsByClubIdSubject.value || {};
        return Object.values(selectedRooms).reduce((total, rooms) => total + rooms.length, 0);
    }

    public handleEmojiPickerButtonClick() {
        this.pos = this.quillEditorHelper.getCaretPositionQuill(this.newPublicationQuillEditor);
        this.isEmojiPickerVisible = !this.isEmojiPickerVisible;
    }

    public handleOnCancel() {
        this.closeModal();
        if (this.isEditMode) this.initValuesIfEditMode();
        this.isTextShown = false;
    }

    public async handleOnSubmit() {
        this.saveMentionMembers();

        if (this.formDoesNotContainContent) {
            this.toastrHelper.showError('Une publication ne peut pas être vide.');
            return;
        }

        this.spinnerHelper.showSpinner();

        try {
            this.submitted = true;
            this.initCommonValues();
            if (this.form.controls.picture.value != null) {
                const file = this.form.controls.picture.value as File;

                const compressedImage = await this.imageService.compressImage(file);
                this.form.controls.picture.setValue(compressedImage);
            }

            if (this.isEditMode) {
                await this.editPublication();
            } else {
                await this.createPublication();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.submitted = false;
        }

        this.spinnerHelper.hideSpinner();
    }

    /**
     * This method handles the paste event in the textarea.
     * It checks if the pasted content is an image or a link.
     * If it is, it retrieves the image as a File or a link and passes it to the appropriate handler.
     *
     * @param {ClipboardEvent} event - The paste event from the textarea.
     */
    public async handlePaste(event: ClipboardEvent): Promise<void> {
        const result = await this.imageService.processClipboardPaste(event);
        if (result) {
            if (result.imageFile) {
                await this.setImageInForm(result.imageFile);
            } else if (result.link) {
                this.setLinkInForm(result.link);
            }
        }
    }

    public handleRemoveImage() {
        if (this.isEditMode) {
            this.pictureHasChanged = true;
            this.cropperParams.urlImage = '';
            this.pictureHasBeenDeleted = true;
        }

        this.localImageSrc = '';
        this.form.controls.image.setValue(null);
        this.form.controls.picture.setValue(null);
    }

    public handleRemoveUrlLink() {
        this.urlLink = '';
        this.form?.controls?.attachedUrl?.setValue(null);
    }

    public imageHasBeenCropped(event: any) {
        this.imageService.imageCropped(event);
        if (!this.isInitialImageLoad) {
            this.pictureHasChanged = true;
        }
        this.isInitialImageLoad = false;
    }

    public initCommonValues() {
        if (this.form.controls.content.value == null) this.form.controls.content.setValue('');
        this.form.controls.userId.setValue(this.currentUser.userId);
        this.form.controls.clubId.setValue(this.clubsStore.displayedRoomId);
    }

    public initValuesIfEditMode() {
        if (this.newPublicationQuillEditor) {
            this.newPublicationQuillEditor.quillEditor.root.innerHTML =
                this.existingPublication?.content?.length > 0 ? this.existingPublication.content : '';
        }

        this.form.controls.postId.setValue(this.existingPublication.id);
        this.form.controls.pictureHasChanged.setValue(false);
        this.form.controls.isAnAnnouncement.setValue(this.existingPublication.isAnAnnouncement);
        this.pictureHasChanged = false;
        this.hasPictureInBegining = this.existingPublication.pictureAccessLink != null;
        this.isInitialImageLoad = true;

        this.localImageSrc = null;
        this.cropperParams.urlImage = this.existingPublication.pictureAccessLink;
        this.pictureHasBeenDeleted = false;

        if (this.existingPublication.attachedDocumentId) {
            this.documentService.getDocument(this.existingPublication.attachedDocumentId, this.clubsStore.displayedRoomId).subscribe((document) => {
                this.LocalDocument = new File([''], document.displayName);
                this.form.controls.attachedDocumentId.setValue(this.existingPublication.attachedDocumentId);
            });
        }

        if (this.existingPublication.pictureAccessLink === null) {
            this.urlLink = this.existingPublication.attachedUrl;
            this.form.controls.attachedUrl.setValue(this.existingPublication.attachedUrl);
        }

        this.form.controls.mentionMembers.setValue(this.existingPublication.mentionMembers);
    }

    public isAdmin(): boolean {
        return this.clubsStore.isAdminOfDisplayedClub;
    }

    public isAnim(): boolean {
        return this.clubsStore.isAnimatorOfDisplayedRoom;
    }

    public ngAfterViewInit() {
        if (this.isEditMode) this.initValuesIfEditMode();
        this.enableMagicUrl();
    }
    public ngOnDestroy(): void {
        this.disableMagicUrl();
    }

    public ngOnInit() {
        this.selectedRoomsService.selectedRooms$.subscribe(() => {
            this.form.updateValueAndValidity();
        });
        this.currentUser = this.accountService.currentUser;
        this.resetForm();

        const titleKey = this.isEditMode ? 'clubs.page.tabs.discussion.post.editTitle' : 'clubs.page.tabs.discussion.post.createTitle';
        this.title = this.translate.instant(titleKey);

        const actionKey = this.isEditMode ? 'components.button.modify' : 'components.button.publish';
        this.action = this.translate.instant(actionKey);

        this.cropperParams.isRatioFix = false;
        this.cropperParams.resizeToHeight = 1000;
        this.cropperParams.resizeToWidth = 1000;

        this.emojiTranslations = this.emojiHelper.getTranslations();
    }

    public onKeydown(event: KeyboardEvent) {
        if (event.key === 'Tab') {
            event.preventDefault();

            const editor = this.newPublicationQuillEditor.quillEditor;

            setTimeout(() => {
                const selection = editor.getSelection();
                if (selection) {
                    editor.insertText(selection.index, '    ');
                    editor.setSelection(selection.index, 4);
                }
            }, 0);
        }
    }

    public onMentionListClosed() {
        this.publicationService.setMentionOpened(false);
    }

    public onMentionListOpen() {
        this.publicationService.setMentionOpened(true);
    }

    public onMentionSelected($event: MentionMember) {
        const value = this.form.controls.mentionMembers.value || [];
        value.push($event);
        this.form.controls.mentionMembers.setValue(value);
    }

    public openModal() {
        this.members = this.clubsStore.flatMembers;
        this.canUserCreateAnnouncement = this.clubsStore.isAdminOfDisplayedClub || this.clubsStore.isAnimatorOfDisplayedRoom;

        if (this.isEditMode) {
            if (this.existingPublication.userId == this.currentUser.userId) {
                this.form.controls.mentionMembers.setValue(this.existingPublication.mentionMembers);
                this.isModalDisplayed = true;
                setTimeout(() => this.enableMagicUrl(), 100);
            } else {
                this.toastrHelper.showError("Vous n'avez pas la permission de modifier ce post.");
            }
        } else {
            if (this.canUserPublish) {
                this.resetForm();
                this.isModalDisplayed = true;
                setTimeout(() => this.enableMagicUrl(), 100);
            } else {
                this.toastrHelper.showError("Vous n'avez pas la permission de publier un post.");
            }
        }
    }

    public plainTextMaxLength(maxLength: number) {
        return (control: AbstractControl): ValidationErrors | null => {
            return this.contentLength > maxLength ? { maxlength: { requiredLength: maxLength, actualLength: this.contentLength } } : null;
        };
    }

    public removeFile() {
        if (this.form.controls.attachedDocumentId) {
            this.documentService
                .deleteDocument({ documentId: this.form.controls.attachedDocumentId.value, clubId: this.clubsStore.displayedRoomId })
                .subscribe();
            this.LocalDocument = null;
            this.form.controls.attachedDocumentId.setValue(null);
            this.clearFileInputs();
        }
    }

    public resetForm() {
        this.form.reset();
        this.localImageSrc = '';
        this.urlLink = '';
        this.form.controls.isAnAnnouncement.setValue(false);
        this.initCommonValues();
        this.form.controls.mentionMembers.setValue(null);
        this.LocalDocument = null;
        this.clearFileInputs();
    }

    public updateDocument(event: any) {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            const reader = new FileReader();
            reader.onload = (event: any) => {
                this.setDocumentInForm(<File>file);
            };
            reader.readAsDataURL(file);
        }
    }

    public async updateImage(event: any) {
        if (event.target.files && event.target.files[0]) {
            const file = event.target.files[0];
            this.setImageInForm(file);
        }
    }

    public validatePostContentOrPicture() {
        if (this.formDoesNotContainContent) {
            return { validatePostContentOrPicture: true };
        } else {
            return null;
        }
    }

    private clearFileInputs(): void {
        // Reset file input elements to allow re-selection of the same file
        if (this.documentInput) {
            this.documentInput.nativeElement.value = '';
        }
        if (this.imageInput) {
            this.imageInput.nativeElement.value = '';
        }
    }

    private async attachDocument() {
        if (this.LocalDocument) {
            // set the url in this.form.attachedDocumentId
            this.form.controls.attachedDocumentId.setValue(
                await this.documentService.uploadDocument(this.clubsStore.displayedRoomId, this.LocalDocument),
            );
        }
    }

    private canUploadDocument(item: DataTransferItem) {
        return item.kind == 'file' && (this.localImageSrc === '' || this.localImageSrc === null) && !this.urlLink;
    }

    private canUploadImage() {
        return !this.urlLink && !this.LocalDocument;
    }

    private canUploadLink(item: DataTransferItem) {
        return (
            (item.type === 'text/plain' || item.type === 'text/html') &&
            (this.localImageSrc === '' || this.localImageSrc === null) &&
            this.LocalDocument === null
        );
    }

    private async createPublication() {
        await this.attachDocument();

        const communityClub = this.clubsStore.clubByCommunityId(this.accountService.currentUser.primaryCommunity.id);

        const shouldCompletePresentation =
            !this.clubsStore.member?.hasCompletedCommunityPresentation &&
            this.clubsStore.displayedClub?.id === communityClub?.id &&
            this.clubsStore.isPresentationRoom;
        const command: CreateMulitplePublicationCommand = {
            ...this.form.value,
            roomIds: this.selectedRoomsService.getSelectedRoomIds(),
        };
        this.publicationApiService
            .createMultiplePublication(command)
            .pipe(
                switchMap((publication) => {
                    this.toastrHelper.showSuccess(this.translate.instant('clubs.page.tabs.discussion.post.postPublished'));
                    this.afterPublicationCreation.emit();
                    this.imageService.initCropperValues();
                    this.closeModal();

                    return shouldCompletePresentation ? this.memberService.completePresentation(this.clubsStore.member.id) : of(null);
                }),
            )
            .subscribe(() => {
                if (shouldCompletePresentation) {
                    this.clubsStore.loadMember();
                }
            });
    }

    private async editPublication() {
        this.form.controls.pictureHasChanged.setValue(this.pictureHasChanged);

        if (this.documentHasChanged) {
            // set the url in this.form.attachedDocumentId
            this.form.controls.attachedDocumentId.setValue(
                await this.documentService.uploadDocument(this.clubsStore.displayedRoomId, this.LocalDocument),
            );
        }

        if (this.isEditingComment) {
            const plainContent = this.quillEditorHelper.getPlainTextWithLineBreaks(this.form.controls.content.value);
            this.form.controls.content.setValue(plainContent);
        } else {
            this.form.controls.content.setValue(this.newPublicationQuillEditor.quillEditor.root.innerHTML);
        }

        const publicationUpdated: Publication = await this.publicationApiService.editPublication(this.form.value).toPromise();

        this.toastrHelper.showSuccess(this.translate.instant('clubs.page.tabs.discussion.post.postModified'));

        this.afterPublicationUpdate.emit(publicationUpdated);
        this.closeModal();
    }

    private isContentEmpty(): boolean {
        return this.form?.controls.content.value == null || this.form?.controls.content.value.length <= 0;
    }

    private isImage(item: DataTransferItem) {
        return item.type.indexOf('image') !== -1;
    }

    private isLocalImageSrcEmpty(): boolean {
        return this.localImageSrc == null || this.localImageSrc == '';
    }

    private isPictureEmpty(): boolean {
        if (this.isEditMode) {
            return this.isPictureEmptyForEditMode();
        } else {
            return this.isPictureEmptyForCreateMode();
        }
    }

    private isPictureEmptyForCreateMode(): boolean {
        return this.form?.controls.picture.value == null && this.isLocalImageSrcEmpty();
    }

    private isPictureEmptyForEditMode(): boolean {
        if (this.hasPictureInBegining && this.pictureHasBeenDeleted) {
            return this.isLocalImageSrcEmpty();
        } else if (!this.hasPictureInBegining) {
            return this.isLocalImageSrcEmpty();
        }
        return false;
    }

    /**
     * Keep and save the members always tagged in the content
     */
    private saveMentionMembers() {
        let value = this.form.controls.mentionMembers.value;
        if (value) {
            const content = this.form.controls.content.value;
            value = value.filter((mention: MentionMember) => content.search(mention.tagMember) > -1);
            this.form.controls.mentionMembers.setValue(value);
        }
    }

    private setDocumentInForm(file: File) {
        if (!this.documentService.hasAcceptedFormat(file.type)) {
            this.toastrHelper.showError(this.translate.instant('toastMessages.fileExtensionError'));
            return;
        }
        this.LocalDocument = file;
        if (this.isEditMode) this.documentHasChanged = true;
    }

    private setImage(file: File) {
        const reader = new FileReader();
        reader.onload = (event: any) => {
            this.localImageSrc = event.target.result;
            if (this.isEditMode) this.pictureHasChanged = true;
            this.form.controls.picture.setValue(<File>file);
        };
        reader.readAsDataURL(file);
    }

    private async setImageInForm(file: File) {
        if (!file) {
            return;
        }
        if (await validateImage(file)) {
            this.setImage(file);
        } else {
            this.toastrHelper.showWarning(this.translate.instant('toastMessages.pictureExtensionError'));
        }
    }

    private setLinkInForm(stringContent: string) {
        if (this.form.controls.attachedUrl.value !== null) {
            return;
        }

        const urlRegex = /(https?:\/\/[^\s]+)/g;

        const matchedUrls = stringContent.match(urlRegex);

        if (matchedUrls.length > 0) {
            this.isLinkLoading = true;
            this.urlLink = matchedUrls[0];
            this.form.controls.attachedUrl.setValue(matchedUrls[0]);
            this.isLinkLoading = false;
        }
    }

    private validateSelectedRooms() {
        const isRoomsEmpty = this.selectedRoomsService.isSelectedRoomsEmpty();
        return isRoomsEmpty ? { noRoomsSelected: true } : null;
    }
    public handleDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
    }

    public handleDragEnter(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        const editorElement = this.newPublicationQuillEditor.editorElem;
        editorElement.classList.add('drag-over');
    }

    public async handleDrop(event: DragEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        const editorElement = this.newPublicationQuillEditor.editorElem;
        editorElement.classList.remove('drag-over');

        if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
            const file = event.dataTransfer.files[0];

            if (file.type.startsWith('image/')) {
                if (this.canUploadImage()) {
                    await this.setImageInForm(file);
                }
            } else if (this.canUploadDocument({ kind: 'file', type: file.type } as DataTransferItem)) {
                this.setDocumentInForm(file);
            }
        } else if (event.dataTransfer && event.dataTransfer.items) {
            for (let i = 0; i < event.dataTransfer.items.length; i++) {
                const item = event.dataTransfer.items[i];
                if (item.kind === 'string' && item.type === 'text/plain') {
                    item.getAsString((text) => {
                        if (this.canUploadLink(item as DataTransferItem)) {
                            this.setLinkInForm(text);
                        } else {
                            const range = this.newPublicationQuillEditor.quillEditor.getSelection();
                            if (range) {
                                this.newPublicationQuillEditor.quillEditor.insertText(range.index, text);
                            }
                        }
                    });
                    break;
                }
            }
        }
    }
    private enableMagicUrl(): void {
        if (this.newPublicationQuillEditor && !this.magicUrlEnabled) {
            this.quillEditorHelper.enableMagicUrl(this.newPublicationQuillEditor, {
                normalizeUrlOptions: {
                    stripWWW: false,
                    forceHttps: true,
                    removeTrailingSlash: false,
                },
            });
            this.magicUrlEnabled = true;
        }
    }
    private disableMagicUrl(): void {
        if (this.newPublicationQuillEditor && this.magicUrlEnabled) {
            this.quillEditorHelper.disableMagicUrl(this.newPublicationQuillEditor);
            this.magicUrlEnabled = false;
        }
    }
}
